import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineMoreHoriz,
  MdOutlinePauseCircle,
  MdOutlinePlayCircle,
  MdOutlineRepeat,
} from 'react-icons/md';
import { Chip, SortDescriptor, TmdxAvatar } from '@ds';
import { ActionMenu, ConditionalRender, MenuItemType, TableColumnType, TruncatedListWrapper } from '@ui';
import {
  OpoContact,
  OpoContactListFilters,
  OpoContactOrderByOption,
  OpoContactTypeFilter,
  OpoPrimaryContactFilter,
  OpoUserInvitationState,
} from '@/api/apis/opo';
import { getContactFullName, getFullName } from '@/utils/helpers';
import { OPO_CONTACT_ROLE_NAMES } from '@/constants/customer.constants';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const OPO_CONTACT_LIST_FILTERS_BASE: OpoContactListFilters = {
  organizationIds: [],
  primaryFilter: OpoPrimaryContactFilter.All,
  contactType: OpoContactTypeFilter.All,
};

export const OPO_CONTACT_LIST_DEFAULT_SORT_BY: SortDescriptor = {
  column: OpoContactOrderByOption.ModifiedAt,
  direction: 'descending',
};

const OPO_CONTACTS_TABLE_COLUMNS: TableColumnType[] = [
  col(OpoContactOrderByOption.FirstName, 'Opo contact name', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('opoName', 'Opo name', {
    sortable: false,
    minWidthClassName: 'min-w-48',
  }),
  col('role', 'Role', {
    sortable: false,
    minWidthClassName: 'min-w-48',
  }),
  col(OpoContactOrderByOption.Email, 'Email', {
    sortable: true,
    minWidthClassName: 'min-w-48',
  }),
  col('primaryPhone', 'Primary phone', {
    sortable: false,
    minWidthClassName: 'min-w-40',
  }),
  col('secondaryPhone', 'Secondary phone', {
    sortable: false,
    minWidthClassName: 'min-w-40',
  }),
  col('isPrimary', 'Is primary contact', {
    sortable: false,
    minWidthClassName: 'min-w-28',
  }),
  col(OpoContactOrderByOption.InvitationState, 'Status', {
    sortable: true,
    minWidthClassName: 'min-w-28',
  }),
];

export const getOpoContactsTableColumns = (hasCreatePermissions: boolean): TableColumnType[] => {
  return hasCreatePermissions
    ? [...OPO_CONTACTS_TABLE_COLUMNS, COMMON_COLUMN_PRESETS.actions]
    : OPO_CONTACTS_TABLE_COLUMNS;
};

const getStatusCell = (contact: OpoContact) => {
  const { isOpoUser, isActive, invitationState } = contact;

  if (!isOpoUser) return <Chip>Contact</Chip>;
  if (!isActive) return <Chip className="bg-danger-60">Deactivated</Chip>;

  switch (invitationState) {
    case OpoUserInvitationState.InviteSent:
      return <Chip>Invite Sent</Chip>;

    case OpoUserInvitationState.Activated:
      return <Chip className="bg-success-60">Active</Chip>;

    default:
      return <Chip>Contact</Chip>;
  }
};

export enum OpoContactActionTypes {
  Edit = 'edit-opo-contact',
  Delete = 'delete-opo-contact',
  ResendInvite = 'resend-invite',
  Activate = 'activate-opo-user',
  Deactivate = 'deactivate-opo-user',
}

const OpoContactActionDataItem: Record<
  OpoContactActionTypes,
  {
    label: string;
    icon: ReactNode;
    className?: string;
  }
> = {
  [OpoContactActionTypes.Edit]: {
    label: 'Edit',
    icon: <MdOutlineEdit size={16} />,
  },
  [OpoContactActionTypes.Delete]: {
    label: 'Delete',
    icon: <MdOutlineDelete size={16} />,
    className: 'text-danger',
  },
  [OpoContactActionTypes.ResendInvite]: {
    label: 'Resend Invite',
    icon: <MdOutlineRepeat size={16} />,
  },
  [OpoContactActionTypes.Activate]: {
    label: 'Activate Account',
    icon: <MdOutlinePlayCircle size={16} />,
  },
  [OpoContactActionTypes.Deactivate]: {
    label: 'Deactivate Account',
    icon: <MdOutlinePauseCircle size={16} />,
  },
};

const getActionItem = (actionType: OpoContactActionTypes, onAction?: () => void): MenuItemType | null => {
  if (!onAction) return null;

  const { icon, label, className } = OpoContactActionDataItem[actionType];

  return {
    key: actionType,
    type: 'action',
    to: actionType,
    label: (
      <div className={`flex items-center gap-2 ${className ?? ''}`}>
        {icon}
        <span className="text-m-r">{label}</span>
      </div>
    ),
    onAction,
  };
};

const compactMenuItems = (items: Array<MenuItemType | null>): MenuItemType[] => {
  return items.filter((item): item is MenuItemType => Boolean(item));
};

type OpoContactActionsMenuProps = {
  contact: OpoContact;
  actions: Partial<{
    onEdit: () => void;
    onDelete: () => void;
    onResendInvite: () => void;
    onActiveChange: (isActive: boolean) => void;
  }>;
};

const getActionsMenu = ({ contact, actions }: OpoContactActionsMenuProps): MenuItemType[] => {
  const { isOpoUser, isActive, invitationState } = contact;
  const { onEdit, onDelete, onResendInvite, onActiveChange } = actions;

  const actionItems: Record<OpoContactActionTypes, MenuItemType | null> = {
    [OpoContactActionTypes.Edit]: getActionItem(OpoContactActionTypes.Edit, onEdit),
    [OpoContactActionTypes.Delete]: getActionItem(OpoContactActionTypes.Delete, onDelete),
    [OpoContactActionTypes.ResendInvite]: getActionItem(OpoContactActionTypes.ResendInvite, onResendInvite),
    [OpoContactActionTypes.Activate]: getActionItem(
      OpoContactActionTypes.Activate,
      onActiveChange ? () => onActiveChange(true) : undefined,
    ),
    [OpoContactActionTypes.Deactivate]: getActionItem(
      OpoContactActionTypes.Deactivate,
      onActiveChange ? () => onActiveChange(false) : undefined,
    ),
  };

  if (!isOpoUser) {
    return compactMenuItems([actionItems[OpoContactActionTypes.Edit], actionItems[OpoContactActionTypes.Delete]]);
  }

  if (invitationState === OpoUserInvitationState.InviteSent) {
    return compactMenuItems([
      actionItems[OpoContactActionTypes.ResendInvite],
      actionItems[OpoContactActionTypes.Edit],
      actionItems[OpoContactActionTypes.Delete],
    ]);
  }

  if (!isActive) {
    return compactMenuItems([
      actionItems[OpoContactActionTypes.Edit],
      actionItems[OpoContactActionTypes.Activate],
      actionItems[OpoContactActionTypes.Delete],
    ]);
  }

  return compactMenuItems([
    actionItems[OpoContactActionTypes.Edit],
    actionItems[OpoContactActionTypes.Deactivate],
    actionItems[OpoContactActionTypes.Delete],
  ]);
};

type RenderOpoContactsTableCellProps = {
  onEdit: (contactId: string) => void;
  onDelete: (contactId: string) => void;
  onResendInvite?: (contact: OpoContact) => void;
  onActiveChange?: (contactId: string, isActive: boolean) => void;
};

export const getRenderOpoContactsTableCell =
  ({ onEdit, onDelete, onResendInvite, onActiveChange }: RenderOpoContactsTableCellProps) =>
  (contact: OpoContact, columnKey: Key): ReactNode => {
    const { id, userId, firstName, lastName, photoPath, email, organizations, primaryPhone, secondaryPhone } = contact;

    const onContactEdit = () => onEdit(id);
    const onContactDelete = () => onDelete(id);

    const onContactResendInvite = userId && onResendInvite ? () => onResendInvite(contact) : undefined;

    const onContactActiveChange = onActiveChange ? (isActive: boolean) => onActiveChange(id, isActive) : undefined;

    const ACTIONS_MENU = getActionsMenu({
      contact,
      actions: {
        onEdit: onContactEdit,
        onDelete: onContactDelete,
        ...(onContactResendInvite && { onResendInvite: onContactResendInvite }),
        ...(onContactActiveChange && { onActiveChange: onContactActiveChange }),
      },
    });

    const isPrimary = organizations?.some((org) => org.isPrimary) ?? false;
    const roles = Array.from(new Set(organizations?.flatMap((org) => org.roles)));

    switch (columnKey) {
      case OpoContactOrderByOption.FirstName:
        return (
          <Link to={`/admin/opos/contacts/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" name={getFullName(firstName, lastName)} src={photoPath ?? undefined} />
            <span className="text-m-m">{getContactFullName(firstName, lastName)}</span>
          </Link>
        );

      case 'opoName':
        return (
          <TruncatedListWrapper wrapperClassName="flex items-center gap-2 max-w-[240px]">
            {organizations?.map((org) => (
              <Chip key={org.id} size="sm">
                <span className="text-m-m text-base-5">{org.name}</span>
              </Chip>
            ))}
          </TruncatedListWrapper>
        );

      case 'role':
        return (
          <TruncatedListWrapper wrapperClassName="flex items-center gap-2 max-w-[240px]">
            {roles.map((role) => (
              <Chip key={role} size="sm">
                <span className="text-m-m text-base-5">{OPO_CONTACT_ROLE_NAMES[role]}</span>
              </Chip>
            ))}
          </TruncatedListWrapper>
        );

      case OpoContactOrderByOption.Email:
        return <span className="text-m-r">{email}</span>;

      case 'primaryPhone':
        return <span className="text-m-r">{primaryPhone}</span>;

      case 'secondaryPhone':
        return <span className="text-m-r">{secondaryPhone}</span>;

      case 'isPrimary':
        return <span className="text-m-r">{isPrimary ? 'Yes' : 'No'}</span>;

      case OpoContactOrderByOption.InvitationState:
        return getStatusCell(contact);

      case 'actions':
        return (
          <ConditionalRender show={!!ACTIONS_MENU.length}>
            <ActionMenu
              sizeClassName="w-[186px]"
              placement="left-start"
              items={ACTIONS_MENU}
              MenuTrigger={
                <div className="">
                  <MdOutlineMoreHoriz size={20} />
                </div>
              }
              label="OPO contact actions menu"
            />
          </ConditionalRender>
        );

      default:
        return null;
    }
  };
